import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
} from '@azure/msal-browser';
import { Observable, filter, map, switchMap, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthInternalService {
  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
  ) {}

  public login(applicationPath?: boolean) {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      tap(() => {
        if (this.hasValidAccount()) {
          this.initActiveAccount();

          const storedReturnUrl = sessionStorage.getItem('returnUrl');
          if (storedReturnUrl) {
            sessionStorage.removeItem('returnUrl');
            this.router.navigateByUrl(decodeURIComponent(storedReturnUrl));
          }
        } else {
          let returnUrl = encodeURI(
            globalThis.location.pathname + globalThis.location.search,
          );

          // applicationPath = till exempel /oreg /sprakresurser etc
          // klipper bort applicationPath, eftersom de inte känns igen i angulars router
          if (applicationPath) {
            if (returnUrl.startsWith('/')) {
              returnUrl = returnUrl.slice(1);
            }
            returnUrl = returnUrl.slice(returnUrl.indexOf('/'));
          }

          sessionStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
          this.authService.loginRedirect();
        }
      }),
    );
  }

  /**
   * Popup-baserad inloggning som kan användas för att lösa inloggning i iframes
   * Notera: bör anropas från en användaråtgärd (t.ex. knapptryck) för att undvika popup-blockerare i webbläsare.
   */
  public loginPopup(
    forceAccountSelection = false,
  ): Observable<AuthenticationResult> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        const request: PopupRequest | undefined = forceAccountSelection
          ? ({ prompt: 'select_account' } as PopupRequest)
          : undefined;
        return this.authService.loginPopup(request);
      }),
      tap((result: AuthenticationResult) => {
        if (result?.account) {
          this.authService.instance.setActiveAccount(result.account);
        }
      }),
    );
  }

  // Sätter aktivt konto i MSAL om giltigt konto finns
  public isLoggedIn(): boolean {
    const hasValid = this.hasValidAccount();
    if (hasValid) {
      this.initActiveAccount();
    }
    return hasValid;
  }

  private hasValidAccount() {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return false;
    }

    const accountForCurrentApp = this.getAccountForCurrentApp();
    if (!accountForCurrentApp) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const exp = (accountForCurrentApp.idTokenClaims as any).exp;
    return exp * 1000 > Date.now();
  }

  private getAccountForCurrentApp() {
    return this.authService.instance.getAllAccounts().find(
      (a) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (a.idTokenClaims as any)?.aud ===
        this.authService.instance.getConfiguration().auth.clientId,
    );
  }

  private initActiveAccount(): void {
    const account = this.getAccountForCurrentApp();
    if (account) {
      this.authService.instance.setActiveAccount(account);
    }
  }
  /**
   *
   * @returns An observable that emits once
   */
  public getIdTokenClaim(claim: string, latestAccount?: boolean) {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return this.getIdTokenClaimInner(claim, latestAccount);
      }),
    );
  }

  public getIdTokenClaimInner(claim: string, latestAccount?: boolean) {
    const account = latestAccount
      ? this.getAccountForCurrentApp()
      : this.authService.instance.getActiveAccount();
    return account?.idTokenClaims[claim];
  }

  public getAccessToken(
    scopes: string[],
    authority: string,
  ): Observable<AuthenticationResult> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() =>
        this.authService.acquireTokenSilent({
          scopes,
          authority: authority,
        }),
      ),
    );
  }

  /**
   * Observable som emitterar en gång när användaren har loggat in.
   * Hanterar INTE redirect själv – förutsätter att MsalGuard eller annan mekanism
   * redan har säkerställt inloggning. Emitterar så snart MSAL är klar och konto finns.
   */
  public awaitLoginAndInitAccount(): Observable<void> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      filter(() => this.hasValidAccount()),
      take(1),
      tap(() => {
        this.initActiveAccount();
      }),
      map((): void => undefined),
    );
  }
}
